<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  activatePrompt,
  copyPrompt,
  generatePrompt,
  promptAudio,
  revertPrompt,
  uploadPrompt,
} from 'dashboard/api/relayVoiceManager';
import {
  slotLabel,
  UPLOAD_ACCEPT,
  uploadProblem,
  versionsFor,
} from './helpers';

const props = defineProps({
  promptSet: { type: String, required: true },
  entry: { type: Object, required: true },
  voice: { type: String, required: true },
  liveVoice: { type: String, required: true },
  library: { type: Array, default: () => [] },
  tts: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['changed']);

const { t } = useI18n();
const { accountId } = useAccount();
const busy = ref('');
const mode = ref('');
const text = ref('');
const reuse = ref('');
const showHistory = ref(false);
const audioUrl = ref('');
const playing = ref('');
const fileInput = ref(null);

const label = computed(() => {
  const l = slotLabel(props.entry.slot);
  if (l.name) return l.name;
  if (l.key === 'DIGIT')
    return t('TEAM_LEAD.PROMPTS.SLOTS.DIGIT', { digit: l.digit });
  if (l.position)
    return t('TEAM_LEAD.PROMPTS.SLOTS.POSITION', { position: l.position });
  return t(`TEAM_LEAD.PROMPTS.SLOTS.${l.key}`);
});
const versions = computed(() => versionsFor(props.entry, props.voice));
// Typing words starts from Squareme's suggested wording for this prompt, if it has one.
const toggleTts = () => {
  mode.value = mode.value === 'tts' ? '' : 'tts';
  if (mode.value === 'tts' && !text.value && props.entry.default_text)
    text.value = props.entry.default_text;
};
const activeUuid = computed(() => props.entry.active?.[props.voice] || null);
const active = computed(() =>
  versions.value.find(v => v.version_uuid === activeUuid.value)
);
const isLive = computed(
  () => props.voice === props.liveVoice && !!active.value
);
const canRevert = computed(() => props.entry.can_revert?.[props.voice]);
const reuseOptions = computed(() =>
  props.library.filter(v => v.version_uuid !== activeUuid.value)
);
const ids = computed(
  () => `${props.promptSet}-${props.entry.slot}-${props.voice}`
);

const sourceText = version => {
  const by = version.created_by_name || t('TEAM_LEAD.PROMPTS.SYSTEM');
  const seconds = (version.duration_ms / 1000).toFixed(1);
  return t(`TEAM_LEAD.PROMPTS.SOURCE.${version.source.toUpperCase()}`, {
    detail: version.source_detail,
    seconds,
    by,
    date: new Date(version.created_at).toLocaleDateString(),
  });
};

const run = async (name, action, success) => {
  busy.value = name;
  try {
    const result = await action();
    useAlert(result?.published ? t('TEAM_LEAD.PROMPTS.LIVE_NOW') : success);
    mode.value = '';
    text.value = '';
    reuse.value = '';
    emit('changed');
  } catch (error) {
    useAlert(error.response?.data?.error || t('TEAM_LEAD.GENERIC_ERROR'));
  } finally {
    busy.value = '';
  }
};

const target = () => ({
  set: props.promptSet,
  slot: props.entry.slot,
  voice: props.voice,
});

const onFile = event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  const problem = uploadProblem(file);
  if (problem) {
    useAlert(t(`TEAM_LEAD.PROMPTS.UPLOAD_PROBLEMS.${problem}`));
    return;
  }
  run(
    'upload',
    () => uploadPrompt(accountId.value, { ...target(), file }),
    t('TEAM_LEAD.PROMPTS.SAVED')
  );
};

const generate = () =>
  run(
    'tts',
    () => generatePrompt(accountId.value, { ...target(), text: text.value }),
    t('TEAM_LEAD.PROMPTS.SAVED')
  );
const useExisting = () =>
  run(
    'reuse',
    () =>
      copyPrompt(accountId.value, { ...target(), versionUuid: reuse.value }),
    t('TEAM_LEAD.PROMPTS.SAVED')
  );
const revert = () =>
  run(
    'revert',
    () => revertPrompt(accountId.value, target()),
    t('TEAM_LEAD.PROMPTS.REVERTED')
  );
const activate = version =>
  run(
    version.version_uuid,
    () => activatePrompt(accountId.value, version.version_uuid),
    t('TEAM_LEAD.PROMPTS.ACTIVATED')
  );

const play = async version => {
  if (audioUrl.value) URL.revokeObjectURL(audioUrl.value);
  audioUrl.value = '';
  playing.value = version.version_uuid;
  try {
    const blob = await promptAudio(accountId.value, version.version_uuid);
    audioUrl.value = URL.createObjectURL(blob);
  } catch (error) {
    playing.value = '';
    useAlert(t('TEAM_LEAD.PROMPTS.PLAY_FAILED'));
  }
};
onBeforeUnmount(() => audioUrl.value && URL.revokeObjectURL(audioUrl.value));

const libraryLabel = version =>
  `${slotLabel(version.slot).name || version.slot} · ${t(`TEAM_LEAD.VOICES.${version.voice.toUpperCase()}`)} · ${(version.duration_ms / 1000).toFixed(1)}s`;
</script>

<template>
  <li class="py-4 flex flex-col gap-3" :data-test-id="`slot-${entry.slot}`">
    <div class="flex items-start justify-between gap-4 flex-wrap">
      <div class="flex flex-col gap-1 min-w-0">
        <div class="flex items-center gap-2 flex-wrap">
          <h4 class="text-sm font-semibold text-n-slate-12">{{ label }}</h4>
          <span
            v-if="isLive"
            class="inline-flex items-center gap-1 rounded-full bg-n-teal-3 px-2 py-0.5 text-xs font-medium text-n-teal-11"
          >
            <span class="size-1.5 rounded-full bg-n-teal-9" />
            {{ t('TEAM_LEAD.PROMPTS.LIVE') }}
          </span>
          <span
            v-else-if="!active"
            class="rounded-full bg-n-amber-3 px-2 py-0.5 text-xs font-medium text-n-amber-11"
          >
            {{
              t('TEAM_LEAD.PROMPTS.NOT_RECORDED', {
                voice: t(`TEAM_LEAD.VOICES.${voice.toUpperCase()}`),
              })
            }}
          </span>
          <span
            v-if="!entry.routed"
            class="rounded-full bg-n-slate-3 px-2 py-0.5 text-xs text-n-slate-11"
          >
            {{ t('TEAM_LEAD.PROMPTS.NOT_ROUTED') }}
          </span>
        </div>
        <p v-if="active" class="text-xs text-n-slate-11">
          {{ sourceText(active) }}
        </p>
        <p
          v-if="active?.spoken_text"
          class="text-xs text-n-slate-10 italic line-clamp-2"
        >
          {{ t('TEAM_LEAD.PROMPTS.QUOTED', { text: active.spoken_text }) }}
        </p>
      </div>
      <div class="flex items-center gap-1.5 flex-wrap">
        <Button
          v-if="active"
          icon="i-lucide-play"
          variant="ghost"
          color="slate"
          size="sm"
          :aria-label="t('TEAM_LEAD.PROMPTS.PLAY', { name: label })"
          @click="play(active)"
        />
        <Button
          :label="t('TEAM_LEAD.PROMPTS.UPLOAD')"
          icon="i-lucide-upload"
          variant="faded"
          color="slate"
          size="sm"
          :is-loading="busy === 'upload'"
          data-tour="prompt-upload"
          @click="fileInput.click()"
        />
        <input
          ref="fileInput"
          type="file"
          class="sr-only"
          :accept="UPLOAD_ACCEPT"
          :aria-label="t('TEAM_LEAD.PROMPTS.UPLOAD_FOR', { name: label })"
          @change="onFile"
        />
        <Button
          v-if="tts.available"
          :label="t('TEAM_LEAD.PROMPTS.TYPE_WORDS')"
          icon="i-lucide-type"
          variant="faded"
          color="slate"
          size="sm"
          data-tour="prompt-tts"
          @click="toggleTts"
        />
        <Button
          v-if="reuseOptions.length"
          :label="t('TEAM_LEAD.PROMPTS.REUSE')"
          icon="i-lucide-copy"
          variant="faded"
          color="slate"
          size="sm"
          @click="mode = mode === 'reuse' ? '' : 'reuse'"
        />
        <Button
          v-if="canRevert"
          :label="t('TEAM_LEAD.PROMPTS.REVERT')"
          icon="i-lucide-undo-2"
          variant="ghost"
          color="slate"
          size="sm"
          :is-loading="busy === 'revert'"
          data-tour="prompt-revert"
          @click="revert"
        />
      </div>
    </div>

    <audio
      v-if="audioUrl && playing"
      :src="audioUrl"
      controls
      autoplay
      class="w-full max-w-md h-9"
      :aria-label="t('TEAM_LEAD.PROMPTS.PREVIEW')"
    />

    <form
      v-if="mode === 'tts'"
      class="flex flex-col gap-2 rounded-lg bg-n-alpha-1 p-3"
      @submit.prevent="generate"
    >
      <label :for="`tts-${ids}`" class="text-xs font-medium text-n-slate-12">
        {{ t('TEAM_LEAD.PROMPTS.TTS_LABEL') }}
      </label>
      <textarea
        :id="`tts-${ids}`"
        v-model="text"
        rows="2"
        maxlength="600"
        class="w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand px-3 py-2 text-sm text-n-slate-12 !mb-0 resize-y"
      />
      <div class="flex items-center justify-between gap-3 flex-wrap">
        <p class="text-xs text-n-slate-10">
          {{
            tts.placeholder
              ? t('TEAM_LEAD.PROMPTS.TTS_PLACEHOLDER')
              : t('TEAM_LEAD.PROMPTS.TTS_VOICE', {
                  name: tts.voices?.[voice] || '',
                })
          }}
        </p>
        <Button
          type="submit"
          :label="t('TEAM_LEAD.PROMPTS.GENERATE')"
          size="sm"
          :disabled="!text.trim()"
          :is-loading="busy === 'tts'"
        />
      </div>
    </form>

    <form
      v-if="mode === 'reuse'"
      class="flex items-end gap-2 flex-wrap rounded-lg bg-n-alpha-1 p-3"
      @submit.prevent="useExisting"
    >
      <div class="flex flex-col gap-1 grow min-w-0">
        <label
          :for="`reuse-${ids}`"
          class="text-xs font-medium text-n-slate-12"
        >
          {{ t('TEAM_LEAD.PROMPTS.REUSE_LABEL') }}
        </label>
        <select
          :id="`reuse-${ids}`"
          v-model="reuse"
          class="!mb-0 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak px-3 py-2 text-sm"
        >
          <option value="" disabled>{{ t('TEAM_LEAD.PROMPTS.CHOOSE') }}</option>
          <option
            v-for="version in reuseOptions"
            :key="version.version_uuid"
            :value="version.version_uuid"
          >
            {{ libraryLabel(version) }}
          </option>
        </select>
      </div>
      <Button
        type="submit"
        :label="t('TEAM_LEAD.PROMPTS.USE')"
        size="sm"
        :disabled="!reuse"
        :is-loading="busy === 'reuse'"
      />
    </form>

    <div v-if="versions.length > 1">
      <button
        type="button"
        class="text-xs font-medium text-n-slate-11 hover:text-n-slate-12 inline-flex items-center gap-1"
        :aria-expanded="showHistory"
        @click="showHistory = !showHistory"
      >
        <span
          class="i-lucide-chevron-right size-3 transition-transform"
          :class="{ 'rotate-90': showHistory }"
        />
        {{ t('TEAM_LEAD.PROMPTS.HISTORY', { n: versions.length }) }}
      </button>
      <ul v-if="showHistory" class="mt-2 flex flex-col gap-1">
        <li
          v-for="version in versions"
          :key="version.version_uuid"
          class="flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-xs"
          :class="
            version.version_uuid === activeUuid
              ? 'bg-n-alpha-2'
              : 'bg-n-alpha-1'
          "
        >
          <span class="text-n-slate-11 truncate">{{
            sourceText(version)
          }}</span>
          <span class="flex items-center gap-1 shrink-0">
            <Button
              icon="i-lucide-play"
              variant="ghost"
              color="slate"
              size="xs"
              :aria-label="t('TEAM_LEAD.PROMPTS.PREVIEW')"
              @click="play(version)"
            />
            <span
              v-if="version.version_uuid === activeUuid"
              class="text-n-teal-11 font-medium px-2"
            >
              {{ t('TEAM_LEAD.PROMPTS.IN_USE') }}
            </span>
            <Button
              v-else
              :label="t('TEAM_LEAD.PROMPTS.USE_VERSION')"
              variant="link"
              size="xs"
              :is-loading="busy === version.version_uuid"
              @click="activate(version)"
            />
          </span>
        </li>
      </ul>
    </div>
  </li>
</template>
